import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';

import { ApiClientService } from '../../../core/http/api-client.service';
import {
  CreateDeviceDowntimeRequest,
  DeleteDeviceDowntimeResponse,
  DeviceDowntimeImpactPreview,
  DeviceDowntimeFilters,
  DeviceDowntimeItem,
  DeviceDowntimeItemDto,
  DeviceDowntimeListSort,
  DeviceDowntimePage,
  DeviceDowntimePageDto,
  DeviceDowntimePendingSummary,
  UpdateDeviceDowntimeRequest
} from './downtime.models';
import { mapDeviceDowntimeItem } from './downtime.mapper';

@Injectable({
  providedIn: 'root'
})
export class DowntimeService
{
  private readonly apiClient = inject(ApiClientService);

  public getList(
    filters?: DeviceDowntimeFilters
  ): Observable<DeviceDowntimeItem[]>
  {
    const query = filters
      ? {
          clinicId: filters.clinicId,
          deviceId: filters.deviceId,
          status: filters.status,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo
        }
      : undefined;

    return this.apiClient.get<DeviceDowntimeItemDto[] | null>(
      '/device-downtime',
      query
    ).pipe(
      map((items: DeviceDowntimeItemDto[] | null) =>
      {
        if (!Array.isArray(items))
        {
          return [];
        }

        return items.map(mapDeviceDowntimeItem);
      })
    );
  }

  public getPage(
    params: {
      limit?: number;
      cursor?: string | null;
      sort?: DeviceDowntimeListSort;
      pendingFirst?: boolean;
      filters?: DeviceDowntimeFilters;
    } = {},
  ): Observable<DeviceDowntimePage>
  {
    return this.apiClient.get<DeviceDowntimePageDto>(
      '/device-downtime',
      {
        limit: params.limit,
        cursor: params.cursor || undefined,
        pendingFirst: params.pendingFirst,
        sort: params.sort,
        clinicId: params.filters?.clinicId,
        deviceId: params.filters?.deviceId,
        status: params.filters?.status,
        dateFrom: params.filters?.dateFrom,
        dateTo: params.filters?.dateTo,
      }
    ).pipe(
      map((page: DeviceDowntimePageDto) => ({
        items: Array.isArray(page.items)
          ? page.items.map(mapDeviceDowntimeItem)
          : [],
        nextCursor: page.nextCursor ?? null,
        hasMore: !!page.hasMore,
      }))
    );
  }

  public getPendingSummary(): Observable<DeviceDowntimePendingSummary>
  {
    return this.apiClient.get<DeviceDowntimePendingSummary>(
      '/device-downtime/pending-summary'
    );
  }

  public getById(
    id: string
  ): Observable<DeviceDowntimeItem>
  {
    return this.apiClient.get<DeviceDowntimeItemDto>(
      `/device-downtime/${id}`
    ).pipe(
      map(mapDeviceDowntimeItem)
    );
  }

  public create(
    payload: CreateDeviceDowntimeRequest
  ): Observable<DeviceDowntimeItem>
  {
    return this.apiClient.post<DeviceDowntimeItemDto, CreateDeviceDowntimeRequest>(
      '/device-downtime',
      payload
    ).pipe(
      map(mapDeviceDowntimeItem)
    );
  }

  public previewImpact(
    payload: CreateDeviceDowntimeRequest
  ): Observable<DeviceDowntimeImpactPreview>
  {
    return this.apiClient.post<DeviceDowntimeImpactPreview, CreateDeviceDowntimeRequest>(
      '/device-downtime/impact-preview',
      payload
    ).pipe(
      map((preview: DeviceDowntimeImpactPreview) => ({
        affectedAppointmentIds: Array.isArray(preview.affectedAppointmentIds)
          ? preview.affectedAppointmentIds
          : [],
        reachabilityImpact: preview.reachabilityImpact ?? null,
      }))
    );
  }

  public update(
    id: string,
    payload: UpdateDeviceDowntimeRequest
  ): Observable<DeviceDowntimeItem>
  {
    return this.apiClient.patch<DeviceDowntimeItemDto, UpdateDeviceDowntimeRequest>(
      `/device-downtime/${id}`,
      payload
    ).pipe(
      map(mapDeviceDowntimeItem)
    );
  }

  public delete(
    id: string
  ): Observable<DeleteDeviceDowntimeResponse>
  {
    return this.apiClient.delete<DeleteDeviceDowntimeResponse>(
      `/device-downtime/${id}`
    );
  }

  public approve(
    id: string
  ): Observable<DeviceDowntimeItem>
  {
    return this.apiClient.patch<DeviceDowntimeItemDto, Record<string, never>>(
      `/device-downtime/${id}/approve`,
      {}
    ).pipe(
      map(mapDeviceDowntimeItem)
    );
  }

  public reject(
    id: string
  ): Observable<DeviceDowntimeItem>
  {
    return this.apiClient.patch<DeviceDowntimeItemDto, Record<string, never>>(
      `/device-downtime/${id}/reject`,
      {}
    ).pipe(
      map(mapDeviceDowntimeItem)
    );
  }

  public resolve(
    id: string
  ): Observable<DeviceDowntimeItem>
  {
    return this.apiClient.patch<DeviceDowntimeItemDto, Record<string, never>>(
      `/device-downtime/${id}/resolve`,
      {}
    ).pipe(
      map(mapDeviceDowntimeItem)
    );
  }
}
